// A multi_region bundle's MESH branches at their neutral pose, as Gaussians.
// Not a second pipeline — the nodes are OGSModel's, minus the rig drive: docs/OGS_EYES.md.

import * as THREE from "three";
import { latestOnly } from "../../lib/latestOnly.js";
import { resolveProviders } from "../../lib/ortSession.js";
import { loadMultiRegionScene } from "../assets/loadMultiRegionScene.ts";
import { fetchBundleJson, loadBundleBytes } from "../assets/urlBundleBytes.ts";
import { prepareOgsLiftDevice, resolveOgsLiftDevice } from "../inference/gpuDevice.ts";
import { ogsGpuFlags } from "../inference/ogsGpuFlags.ts";
import * as ogsOrt from "../inference/ort.ts";
import { LocalFrameCamera } from "../render/localFrameCamera.ts";
import { SplatMeshUpdater } from "../render/SplatMeshUpdater.ts";
import { worldRotationQuaternion } from "../render/worldRotation.ts";
import { OGSBranch } from "./nodes/OGSBranch.ts";
import { buildBranches, renderSources } from "./buildBranches.ts";
import { OGSRenderer } from "./nodes/OGSRenderer.ts";

export interface NeutralBranchSystemOptions {
  scene: THREE.Object3D;
  camera: THREE.PerspectiveCamera;
  /** URL directory holding scene.json and the files it names. */
  base: string;
  /** Already-parsed scene.json; fetched from `base` when omitted. */
  sceneJson?: unknown;
  /** Subscribed for 'change': appr is view-dependent, so an orbit is a re-render. */
  controls?: { addEventListener?: Function; removeEventListener?: Function } | null;
  /** False parks the passes — a scene teardown mid-flight. */
  isActive?: () => boolean;
}

export type NeutralBranchSystem = Awaited<ReturnType<typeof createNeutralBranchSystem>>;

/** The branch's two terminal UV ports, as the renderer's source pair. */
function portPair(branch: OGSBranch): [string, string] {
  const [geom, color] = branch.outputs.map((port) => port.name);
  return [geom, color];
}

export async function createNeutralBranchSystem(options: NeutralBranchSystemOptions) {
  const { scene, camera, base } = options;
  const isActive = options.isActive ?? (() => true);
  const flags = ogsGpuFlags();
  const providers = await resolveProviders(flags.ep, { preferWebgpu: true });

  const sceneJson = options.sceneJson ?? await fetchBundleJson(base, "scene.json");
  const parsed = await loadMultiRegionScene(base, sceneJson);
  if (!parsed.branches.length) {
    throw new Error(`neutral branches: ${base}/scene.json declares no mesh branch `
      + "(a gaze-conditioned branch is the eye system's, not this one's)");
  }

  // ONE quaternion for both consumers — the mesh turns by it, the plücker camera by
  // its inverse, and they must be the same rotation (render/worldRotation.ts).
  const worldRotation = worldRotationQuaternion(parsed.manifest.worldRotation);

  // fp32 only: probing for the optional fp16 siblings costs a round trip per decoder.
  const modelNames = parsed.branches.flatMap(({ spec }) =>
    [spec.trunk, spec.geom, spec.appr].filter((name): name is string => !!name));
  const bytes = await loadBundleBytes(base, modelNames);

  // Before the first session create; resolveOgsLiftDevice after. Order is load-bearing (gpuDevice.ts).
  const prepared = await prepareOgsLiftDevice(ogsOrt, providers, flags);

  const built = await buildBranches({
    branchAssets: parsed.branches,
    ort: ogsOrt,
    prepared,
    idPrefix: "neutral",
    live: () => ({
      providers,
      getBytes: bytes.getBytes,
      fetchBytes: bytes.fetchBytes,
      originScale: 1 / parsed.manifest.worldScale,
      worldRotation,
      flags,
    }),
  });
  if (!built) throw new Error("neutral branches: build cancelled");
  const { branches, device, contextFor } = built;

  const group = new THREE.Group();
  group.name = "neutral-branches";
  scene.add(group);
  const updater = new SplatMeshUpdater(group);
  updater.meshScale = parsed.manifest.worldScale;
  updater.meshOffset = parsed.manifest.worldOffset;
  updater.meshRotation = worldRotation;

  // The class, not ogsRendererPlugin.create(): the factory publishes onto the live avatar's singleton.
  const renderer = new OGSRenderer({
    id: "neutral_renderer",
    updater,
    sources: renderSources(branches),
  } as never);
  await renderer.init(contextFor(branches[0], device));

  const localCamera = new LocalFrameCamera();
  let disposed = false;
  let neutralDone = false;
  let visible = true;
  let passes = 0;
  let lastError: string | null = null;

  // One pass over every branch, then ONE present of the concatenation.
  const runPass = async (mode: "full" | "appearance") => {
    if (disposed || !isActive()) return;
    if (mode === "appearance" && !neutralDone) return;
    const inbound: Record<string, unknown> = {};
    for (const branch of branches) {
      const uvs = mode === "full"
        ? await branch.renderFull(branch.neutralRig(), camera)
        : await branch.renderAppearance(camera);
      if (disposed) return;
      const [geomPort, colorPort] = portPair(branch);
      inbound[geomPort] = uvs.geom_uv;
      inbound[colorPort] = uvs.color_uv;
    }
    await renderer.evaluate(
      { rig: null, camera: localCamera.update(camera, group), rigEpoch: 0, cameraEpoch: 0 } as never,
      inbound as never);
    if (mode === "full") neutralDone = true;
    passes++;
  };

  // Coalesced: a burst of camera moves costs one extra pass, at the newest view.
  const relight = latestOnly(async () => {
    try {
      await runPass("appearance");
      lastError = null;
    } catch (error: unknown) {
      lastError = (error as Error)?.message ?? String(error);
      console.error("[neutral-branches] appearance pass failed", error);
    }
  });

  const onCameraChange = () => { void relight(); };
  options.controls?.addEventListener?.("change", onCameraChange);

  await runPass("full");
  console.info(`[neutral-branches] ${renderer.lastCount} splats from `
    + `${branches.map((branch) => branch.name).join("+")} — `
    + branches.map((branch) => `${branch.name} uv=${branch.uvRes} lift=${branch.liftKind()} `
      + `plucker=${branch.pluckerKind()}`).join(", "));

  return {
    branches: branches.map((branch) => branch.name),

    setVisible(next: boolean) {
      visible = !!next;
      updater.setVisible(visible);
    },

    /** Re-run appearance at the live camera. Coalesced; safe to call per frame. */
    relight: () => relight(),

    /** Diagnostics, built fresh per read: fine for a 100 ms poll, wrong per frame. */
    get state() {
      return {
        count: renderer.lastCount,
        passes,
        visible,
        worldScale: parsed.manifest.worldScale,
        lastError,
        branches: branches.map((branch) => ({
          name: branch.name,
          uvResolution: branch.uvRes,
          vertices: branch.neutralVertices.length / 3,
          lift: branch.liftKind(),
          plucker: branch.pluckerKind(),
          geom: branch.geomUsedFp16 ? "fp16" : "fp32",
          appr: branch.apprUsedFp16 ? "fp16" : "fp32",
        })),
      };
    },

    dispose() {
      disposed = true;
      options.controls?.removeEventListener?.("change", onCameraChange);
      renderer.dispose();
      updater.dispose();
      for (const branch of branches) branch.dispose();
      scene.remove(group);
    },
  };
}
